import { SymbolType } from '../../../../../domain/symbol.domain';
import { TradingAccount } from '../../../../../domain/account.domain';
import { MarketType, MarketTypeHelper } from '../../../../../domain/market.domain';
import {
  BaseExtendedTerms,
  EquityOptionsCommonModel,
  InsiderType,
  OrderDateType,
  OrderExtendedTerms,
  PriceType
} from '../../../domain';
import { OrderFormParameters, OrderStateParameters } from '../../domain/order-entry.domain';
import { CreateOrderFormService } from './create-order-form.service';

export abstract class CreateOrderFormStocksOptionsBaseFormService<T extends EquityOptionsCommonModel>
  extends CreateOrderFormService<T> {

  protected initFromParameters(selectedAccount?: TradingAccount,
                               orderFormParameters?: OrderFormParameters): void {
    if (orderFormParameters?.orderStateParameters !== undefined) {
      const parameters = this.filterParameters(orderFormParameters.orderStateParameters, selectedAccount);
      if (parameters.quantity !== undefined) {
        this.setQuantity(parameters.quantity, parameters.action);
      }
      if (parameters.symbol !== undefined && parameters.market !== undefined) {
        this.setOrderSymbol({
          symbol: parameters.symbol,
          market: parameters.market,
          optionNonStandard: String(parameters.optionNonStandard) === 'true'
          // ^- even though the domain is bool we must accept 'true/false' and the route is defined as string.
          // so this causes the issues here where we will convert to proper bool (as boolean wont work)
        });
      }
      if (parameters.action !== undefined) {
        this.setOrderFormAction({
          action: parameters.action,
          quantity: parameters.quantity
        });
      }
      if (parameters.price !== undefined) {
        this.setOrderFormPriceType({
          orderType: parameters.price
        } as OrderExtendedTerms);
      }
      this.setParameters(parameters);
    }
  }

  protected setSymbol(symbolParams: { symbol: string; market: MarketType; symbolType: SymbolType }): void {
    if (symbolParams.symbol !== undefined &&
      symbolParams.market !== undefined &&
      symbolParams.symbolType !== undefined) {
      let symbolType;
      if (symbolParams.symbolType === SymbolType.EQUITY || symbolParams.symbolType === SymbolType.STOCK) {
        symbolType = SymbolType.STOCK;
      } else if (symbolParams.symbolType === SymbolType.OPTION) {
        symbolType = SymbolType.OPTION;
      }
      this.temp.selectedSymbol = {
        symbol: symbolParams.symbol,
        market: MarketTypeHelper.fromString(symbolParams.market),
        type: symbolType
      };
    }
  }

  protected setPriceAmount(extendedTerms: { orderType: PriceType } & OrderExtendedTerms): void {
    if (extendedTerms.orderType !== PriceType.MARKET) {
      this.temp.orderPriceAmount = {
        priceLimit: extendedTerms.limitPrice,
        priceTrigger: extendedTerms.triggerPrice
      };
    }
  }

  protected setOrderFormGoodTil(extendedTerms: BaseExtendedTerms): void {
    if (extendedTerms.goodTill === OrderDateType.DAY) {
      this.temp.orderGoodTil = extendedTerms.goodTill;
    } else {
      this.temp.orderGoodTil = extendedTerms.goodTill;
      this.temp.orderGoodTilDate = extendedTerms.goodTillDate;
    }
  }

  protected setOrderShareholderStatus(clientInfo: { insiderInfo: InsiderType; solicited: boolean }): void {
    if (clientInfo !== undefined && clientInfo.insiderInfo !== null) {
      this.temp.shareholderStatus = clientInfo.insiderInfo;
    }
  }

  protected setOrderAllOrNone(extendedTerms: OrderExtendedTerms): void {
    this.temp.orderAllOrNone = extendedTerms.allOrNone;
  }

  private setQuantity(quantity: number, action: string | undefined): void {
    if (quantity !== undefined) {
      if (action !== undefined && (action === 'sell' || action === 'short_sell') && quantity > 0 ||
        (action === 'buy' || action === 'buy_to_cover') && quantity < 0) {
        this.temp.orderQuantity = Math.abs(quantity); // TODO review: does it mean we only assign quantity when action is correct ?
      }
    }
  }

  protected abstract setOrderSymbol(parameters: Pick<OrderStateParameters, 'symbol' | 'market' | 'optionNonStandard'>): void;
  protected abstract setOrderFormAction(parameters: Pick<OrderStateParameters, 'action' | 'quantity'>): void;
  protected abstract setOrderFormPriceType(extendedTerms: OrderExtendedTerms): void;
  protected abstract setParameters(parameters: OrderStateParameters): void;

}

